import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search, MapPin } from 'lucide-react';
import { NavLink } from 'react-router-dom';

export default function NotFound() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Page Not Found — CROSSDOCS</title>
        <meta name="description" content="The page you're looking for couldn't be found. Return to CROSSDOCS homepage or explore our services." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container-consistent text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-8xl md:text-9xl font-bold text-primary-400 mb-6">404</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Page Not Found
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Sorry, we couldn't find the page you're looking for. It might have been moved, 
              deleted, or you entered the wrong URL.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/" 
                className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:bg-primary-700 hover:shadow-xl transition-all duration-300"
              >
                <Home className="h-5 w-5" />
                Go Home
              </NavLink>
              <button 
                onClick={() => window.history.back()}
                className="inline-flex items-center gap-2 rounded-xl px-8 py-4 text-lg font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                <ArrowLeft className="h-5 w-5" />
                Go Back
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Need Help Finding Something?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Let us help you navigate to the right page or find the information you're looking for.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-3">
            {[
              {
                icon: Search,
                title: 'Search Our Site',
                description: 'Use our search functionality to find specific content or services.',
                action: 'Search Now',
                link: '/'
              },
              {
                icon: MapPin,
                title: 'Site Navigation',
                description: 'Explore our main navigation to find the right section.',
                action: 'View Services',
                link: '/services'
              },
              {
                icon: Home,
                title: 'Start Over',
                description: 'Return to our homepage and navigate from there.',
                action: 'Go Home',
                link: '/'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 mb-6">{item.description}</p>
                <NavLink
                  to={item.link}
                  className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300"
                >
                  {item.action}
                </NavLink>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Pages */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Popular Pages
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Here are some of our most visited pages that might contain what you're looking for.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { name: 'Services', link: '/services', description: 'Our logistics solutions' },
              { name: 'About Us', link: '/about', description: 'Learn about CROSSDOCS' },
              { name: 'Contact', link: '/contact', description: 'Get in touch with us' },
              { name: 'Industries', link: '/industries', description: 'Industries we serve' }
            ].map((page, index) => (
              <motion.div
                key={page.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-4 border border-slate-200 hover:shadow-md transition-all duration-300"
              >
                <NavLink
                  to={page.link}
                  className="block text-center group"
                >
                  <h3 className="font-semibold text-slate-900 group-hover:text-primary-600 transition-colors duration-200">
                    {page.name}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">{page.description}</p>
                </NavLink>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}






